import React, { useState, useEffect, useCallback } from 'react';
import { Save, Plus, Trash2 } from 'lucide-react';
import { Gate } from '../../AccessStudio';

// Competency grid (French primary system): students × competencies,
// each cell one of the CompetencyLevel lookup values
// (Acquired / In Progress / Not Acquired) — cycled by clicking.
const LEVEL_STYLE = {
  'Acquired': 'bg-green-600 text-white',
  'In Progress': 'bg-amber-500 text-white',
  'Not Acquired': 'bg-red-600 text-white'
};
const LEVEL_SHORT = { 'Acquired': 'A', 'In Progress': 'EC', 'Not Acquired': 'NA' };

const CompetencySheet = ({ apiCall, year, className, mappings, periods, showSuccess, showError }) => {
  const [classSubjectId, setClassSubjectId] = useState('');
  const [periodId, setPeriodId] = useState('');
  const [sections, setSections] = useState([]);
  const [sectionCode, setSectionCode] = useState('');
  const [levels, setLevels] = useState(['Acquired', 'In Progress', 'Not Acquired']);
  const [sheet, setSheet] = useState(null);
  const [marks, setMarks] = useState({}); // `${studentId}|${competencyId}` -> level
  const [dirty, setDirty] = useState(false);
  const [newComp, setNewComp] = useState('');

  useEffect(() => {
    setClassSubjectId(prev => mappings.some(m => String(m.ClassSubjectID) === String(prev)) ? prev : (mappings[0]?.ClassSubjectID || ''));
  }, [mappings]);
  useEffect(() => {
    setPeriodId(prev => periods.some(p => String(p.PeriodID) === String(prev)) ? prev : (periods[0]?.PeriodID || ''));
  }, [periods]);

  useEffect(() => {
    apiCall('/registration/lookups').then(l => {
      if (l.CompetencyLevel?.length) setLevels(l.CompetencyLevel);
    }).catch(() => {});
  }, [apiCall]);

  useEffect(() => {
    if (!className) return;
    apiCall(`/academic/sections?academicYear=${year}&className=${encodeURIComponent(className)}`)
      .then(s => setSections(s.filter(x => x.IsActive))).catch(() => setSections([]));
    setSectionCode('');
  }, [apiCall, year, className]);

  const load = useCallback(async () => {
    if (!classSubjectId || !periodId) { setSheet(null); return; }
    try {
      let url = `/academic/grading/competency-sheet?classSubjectId=${classSubjectId}&periodId=${periodId}`;
      if (sectionCode) url += `&sectionCode=${encodeURIComponent(sectionCode)}`;
      const data = await apiCall(url);
      setSheet(data);
      const map = {};
      data.marks.forEach(m => { map[`${m.StudentID}|${m.CompetencyID}`] = m.Level; });
      setMarks(map);
      setDirty(false);
    } catch (err) { showError(err.message); }
  }, [apiCall, classSubjectId, periodId, sectionCode, showError]);

  useEffect(() => { load(); }, [load]);

  const cycle = (studentId, competencyId) => {
    const key = `${studentId}|${competencyId}`;
    setMarks(ms => {
      const cur = ms[key];
      const idx = levels.indexOf(cur);
      const next = idx === -1 ? levels[0] : (idx === levels.length - 1 ? null : levels[idx + 1]);
      return { ...ms, [key]: next };
    });
    setDirty(true);
  };

  const save = async () => {
    try {
      const entries = [];
      for (const stu of sheet.students) {
        for (const c of sheet.competencies) {
          const key = `${stu.StudentID}|${c.CompetencyID}`;
          if (key in marks) entries.push({ studentId: stu.StudentID, competencyId: c.CompetencyID, level: marks[key] || null });
        }
      }
      const r = await apiCall('/academic/grading/competency-marks', {
        method: 'POST',
        body: JSON.stringify({ classSubjectId: parseInt(classSubjectId), periodId: parseInt(periodId), entries })
      });
      showSuccess(r.message);
      setDirty(false);
    } catch (err) { showError(err.message); }
  };

  const addCompetency = async () => {
    if (!newComp.trim()) return;
    try {
      await apiCall('/academic/grading/competencies', {
        method: 'POST',
        body: JSON.stringify({ classSubjectId: parseInt(classSubjectId), competencyText: newComp, sortOrder: (sheet?.competencies?.length || 0) + 1 })
      });
      showSuccess('Competency added');
      setNewComp('');
      load();
    } catch (err) { showError(err.message); }
  };

  const deleteCompetency = async (c) => {
    if (!window.confirm(`Delete "${c.CompetencyText}" and all its marks?`)) return;
    try {
      await apiCall(`/academic/grading/competencies/${c.CompetencyID}`, { method: 'DELETE' });
      showSuccess('Competency deleted');
      load();
    } catch (err) { showError(err.message); }
  };

  if (!mappings.length) {
    return <div className="bg-white rounded-lg shadow p-8 text-center text-gray-400 text-sm">
      No subjects assigned to {className} yet — add them in Exams Setup, then attach competencies here.</div>;
  }

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow p-4 flex flex-wrap items-center gap-3">
        <select value={classSubjectId} onChange={e => setClassSubjectId(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
          {mappings.map(m => <option key={m.ClassSubjectID} value={m.ClassSubjectID}>{m.SubjectName}</option>)}
        </select>
        <select value={periodId} onChange={e => setPeriodId(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
          {periods.map(p => <option key={p.PeriodID} value={p.PeriodID}>{p.PeriodName}{p.IsLocked ? ' 🔒' : ''}</option>)}
        </select>
        <select value={sectionCode} onChange={e => setSectionCode(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
          <option value="">All sections</option>
          {sections.map(s => <option key={s.SectionID} value={s.SectionCode}>{className} {s.SectionCode}</option>)}
        </select>
        <span className="text-xs text-gray-400">click a cell to cycle: {levels.join(' → ')}</span>
        <div className="flex-1" />
        <Gate permission="academic.grading.enter" label="Save Competency Marks">
          <button onClick={save} disabled={!dirty}
            className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1">
            <Save size={15} /> Save Marks
          </button>
        </Gate>
      </div>

      <Gate permission="academic.grading.configure" label="Add Competency">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-end gap-2">
          <div className="flex-1">
            <label className="block text-xs text-gray-500 mb-1">New competency for this subject</label>
            <input type="text" value={newComp} onChange={e => setNewComp(e.target.value)}
              placeholder="e.g. Lire un texte simple à voix haute"
              className="w-full border rounded-lg px-3 py-1.5 text-sm" />
          </div>
          <button onClick={addCompetency} className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 flex items-center gap-1">
            <Plus size={14} /> Add
          </button>
        </div>
      </Gate>

      {sheet && sheet.competencies.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
          No competencies defined for this subject yet — add them above.
        </div>
      )}

      {sheet && sheet.competencies.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase whitespace-nowrap">Student</th>
                {sheet.competencies.map(c => (
                  <th key={c.CompetencyID} className="px-1 py-2 text-center font-normal text-gray-600 max-w-[120px]">
                    <div className="flex flex-col items-center gap-1">
                      <span className="leading-tight">{c.CompetencyText}</span>
                      <Gate permission="academic.grading.configure" label="Delete Competency">
                        <button onClick={() => deleteCompetency(c)} className="text-red-300 hover:text-red-600"><Trash2 size={11} /></button>
                      </Gate>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sheet.students.map(stu => (
                <tr key={stu.StudentID} className="hover:bg-gray-50">
                  <td className="px-3 py-1 font-medium text-gray-800 whitespace-nowrap">
                    {stu.LastName} {stu.FirstName}
                    {!sectionCode && stu.Section && <span className="ml-1 text-gray-400">({stu.Section})</span>}
                  </td>
                  {sheet.competencies.map(c => {
                    const lv = marks[`${stu.StudentID}|${c.CompetencyID}`];
                    return (
                      <td key={c.CompetencyID} className="px-1 py-1 text-center">
                        <button
                          onClick={() => cycle(stu.StudentID, c.CompetencyID)}
                          title={lv || 'not marked'}
                          className={`w-9 h-6 rounded text-[10px] font-bold ${lv ? LEVEL_STYLE[lv] || 'bg-gray-500 text-white' : 'bg-gray-100 text-gray-400'}`}
                        >
                          {lv ? (LEVEL_SHORT[lv] || lv.slice(0, 2)) : '·'}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CompetencySheet;
